import dayjs, { ConfigType, Dayjs, ManipulateType, OpUnitType, QUnitType } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration, { Duration, DurationUnitsObjectType } from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localeData from 'dayjs/plugin/localeData';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import { DisabledDates, TimeFormats } from '@/constants/enums';
import { locale } from '@/constants/global';
import { UTC_FORMAT, YEAR_MONTH_DAY_TIME_WITH_MILISECONDS_FORMAT } from '@/OptimusRoutes/constants';
import {
	DateRange,
	DAY,
	DaysOfWeek,
	IsSameDay,
	StartAndEndOfDay,
	UtcToLocal,
	ValidateIntervalDates,
} from '@/utilities/dateUtils.type';
import 'dayjs/locale/es';

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(updateLocale);

dayjs.locale(locale.en_US.intl);

dayjs.updateLocale('es', {
	weekdays: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
	months: [
		'Enero',
		'Febrero',
		'Marzo',
		'Abril',
		'Mayo',
		'Junio',
		'Julio',
		'Agosto',
		'Septiembre',
		'Octubre',
		'Noviembre',
		'Diciembre',
	],
});

export const getNormalizedDateTime = (date?: dayjs.ConfigType, format?: dayjs.OptionType): dayjs.Dayjs => {
	return dayjs(date, format);
};

export const guessTimeZone = () => {
	return dayjs.tz.guess();
};

export const getJsDate = (date?: dayjs.ConfigType): Date => {
	return dayjs(date).toDate();
};

// ! this function must be removed, use getNormalizedDateTime() instead
export const stringToDayjs = (dateString: string): dayjs.Dayjs => {
	const converttedDate = dayjs(dateString);
	return converttedDate;
};

export const dateToStringWithOutTimezone = (
	date: dayjs.ConfigType,
	format: string = YEAR_MONTH_DAY_TIME_WITH_MILISECONDS_FORMAT
): string => {
	return dayjs(date).utc(false).format(format);
};

export const getFormattedDate = (date?: Date | string, format = 'YYYY-MM-DD'): Date => {
	const getDate = dayjs(date).format(format);
	return dayjs(getDate).toDate();
};

export const formatDate = (format = 'YYYY-MM-DD', normalDate?: Date | string, isUtc = false): string => {
	const date = isUtc ? dayjs.utc(normalDate).local() : dayjs(normalDate);
	const formattedDate = date.format(format);
	return formattedDate;
};

export const getTimeDifference = (
	oldDate: ConfigType,
	newDate: ConfigType = new Date(),
	unit: QUnitType | OpUnitType = 'hour',
	lang: string | ILocale | undefined = 'en',
	floatValue: boolean = false
): number => {
	dayjs.locale(lang);
	const getTime = dayjs(newDate);
	const result = getTime.diff(oldDate, unit, floatValue);
	return result;
};

export const getDurationBetweenDates = (date1: ConfigType, date2: ConfigType): Duration => {
	const timeDifference = dayjs(date2).diff(dayjs(date1));
	const duration = dayjs.duration(timeDifference);

	return duration;
};

export const getFormattedDuration = (duration: Duration, format?: string): string => {
	const durationFormatted = duration.format(format);

	return durationFormatted;
};

// ! this will no be used anymore, use getNormalizedDate instead
export const getCurrentDate = (): dayjs.Dayjs => {
	return dayjs();
};

export const getUtc = (date: dayjs.Dayjs = dayjs()): dayjs.Dayjs => {
	return date.utc();
};

/**
 * Creates a Day.js object in UTC (parses input as UTC. Assumes the given value is already in UTC).
 * @param {dayjs.ConfigType} config - UTC input to create the UTC date (string, Date, timestamp, etc.)
 * @returns {dayjs.Dayjs} A Day.js object in UTC mode
 */
export const parseDateToUtcFormat = (config?: ConfigType, format?: string): dayjs.Dayjs => {
	return dayjs.utc(config, format);
};

/**
 * Converts the input **from local time to UTC** (or uses current local time if no input is given).
 * @param {dayjs.ConfigType} date - Optional date input to convert (defaults to now)
 * @returns {dayjs.Dayjs} A Day.js object in UTC mode
 */
export const toUtcDate = (date?: dayjs.ConfigType, format?: string): dayjs.Dayjs => {
	return dayjs(date, format).utc();
};

export const getUtcStartAndEndOfDate = (
	date: string | Date,
	dateFormat: string,
	unit: dayjs.OpUnitType = 'day'
): StartAndEndOfDay<Date> => {
	const utcDate = typeof date === 'string' ? dayjs.utc(date, dateFormat) : dayjs.utc(date);
	const startOfDay = utcDate.startOf(unit).format(YEAR_MONTH_DAY_TIME_WITH_MILISECONDS_FORMAT);
	const endOfDay = utcDate.endOf(unit).format(YEAR_MONTH_DAY_TIME_WITH_MILISECONDS_FORMAT);

	return { startOfDate: new Date(startOfDay), endOfDate: new Date(endOfDay) };
};

export const utcStartAndEndOfDay = (
	date: dayjs.ConfigType,
	inputFormat: dayjs.OptionType
): StartAndEndOfDay<string> => {
	const startOfDayUTC = dayjs(date, inputFormat).startOf('day').utc().format(UTC_FORMAT);
	const endOfDayUTC = dayjs(date, inputFormat).endOf('day').utc().format(UTC_FORMAT);

	return { startOfDate: startOfDayUTC, endOfDate: endOfDayUTC };
};

export const getStartOfDay = (date: string): Date => {
	const dateValue = dayjs(date).startOf('day');
	const startOfDayDate = dateValue.toDate();
	return startOfDayDate;
};

// todo: avoid using this function, use getEndOf() instead.
export const getEndOfDay = (date: string): Date => {
	const dateValue = dayjs(date).endOf('day');
	const startOfDayDate = dateValue.toDate();
	return startOfDayDate;
};

export const getUtcOffset = (): number => {
	return dayjs().utcOffset();
};

export const toLocalDate = (date: dayjs.Dayjs = dayjs()) => {
	return date.local();
};

export const getDateUtcWithOffset = (
	date: dayjs.Dayjs = dayjs(),
	offset: number | string,
	keepLocalTime?: boolean
): dayjs.Dayjs => {
	return date.utcOffset(offset, keepLocalTime);
};

export const formatTimezoneOffset = (utcOffsetMinutes: number): string => {
	const formattedOffset = dayjs().utcOffset(utcOffsetMinutes).format('Z');
	return formattedOffset;
};

export const getElapsedTimeInUtc = (utcDate: Date): string => dayjs().to(dayjs(utcDate));

export const isNewDate = (dateToCheck?: ConfigType, baseDate?: ConfigType, unit: OpUnitType = 'seconds'): boolean => {
	return dayjs.utc(dateToCheck).isAfter(dayjs.utc(baseDate), unit);
};

export const dateToUtc = (
	normalDate: dayjs.ConfigType,
	format = YEAR_MONTH_DAY_TIME_WITH_MILISECONDS_FORMAT,
	includeSeconds = true
): string => {
	const utcDate = dayjs(normalDate).utc();

	if (includeSeconds) {
		return utcDate.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
	} else {
		return `${utcDate.format('YYYY-MM-DDTHH:mm')}:00.000Z`;
	}
};

export const utcToLocalTimezone = (utcDate: ConfigType, format: string = 'YYYY-MM-DD'): string =>
	dayjs.utc(utcDate).local().format(format);

export const utcToLocalTime = (date: ConfigType, format?: string): dayjs.Dayjs => {
	const localDate = dayjs.utc(date).local();
	const localTimeWithFormat = dayjs(localDate, format);

	return format ? localTimeWithFormat : localDate;
};

export const dateToUtcFormatted = (
	normalDate: dayjs.ConfigType,
	outputFormat = YEAR_MONTH_DAY_TIME_WITH_MILISECONDS_FORMAT
): string => {
	return dayjs(normalDate).utc().format(outputFormat);
};

export const applyUtcOffsetAndFormat = (
	date: ConfigType,
	utcOffsetMinutes: number,
	format: string = YEAR_MONTH_DAY_TIME_WITH_MILISECONDS_FORMAT,
	unit: dayjs.ManipulateType | undefined = 'minutes'
): string => dayjs.utc(date).add(utcOffsetMinutes, unit).format(format);

export const subtractUtcoffsetMinutesAndFormat = (
	date: ConfigType,
	utcOffsetMinutes: number,
	format: string = YEAR_MONTH_DAY_TIME_WITH_MILISECONDS_FORMAT,
	unit: dayjs.ManipulateType | undefined = 'minutes'
): string => dayjs.utc(date).subtract(utcOffsetMinutes, unit).format(format);

export const getDateFromNow = (
	utcDate: ConfigType,
	utcOffsetMinutes?: string | number,
	lang: string | ILocale | undefined = 'en'
): string => {
	dayjs.locale(lang);
	let formattedDate;
	if (utcOffsetMinutes) {
		formattedDate = dayjs.utc(utcDate).utcOffset(utcOffsetMinutes).fromNow();
	} else {
		formattedDate = dayjs.utc(utcDate).local().fromNow();
	}

	return formattedDate;
};

export const getTime = (): number => dayjs().hour();

export const getDateInMilliseconds = (date?: dayjs.ConfigType): number => dayjs(date).valueOf();

/**
 * Converts a date to a formatted string using dayjs.
 *
 * This utility function can be used in multiple ways depending on the parameters provided:
 *
 * 1. **dateToString(newFormat: string): string**
 *    - Returns the current date formatted with the given format.
 *    - Example:
 *      ```ts
 *      dateToString('YYYY-MM-DD');
 *      ```
 *
 * 2. **dateToString(date: dayjs.ConfigType): string**
 *    - Returns the given date formatted in default ISO format.
 *    - Example:
 *      ```ts
 *      dateToString('2025-04-15');
 *      dateToString(new Date());
 *      ```
 *
 * 3. **dateToString(date: dayjs.ConfigType, newFormat?: string): string**
 *    - Returns the given date formatted with the specified format.
 *    - Example:
 *      ```ts
 *      dateToString(new Date(), 'MMM D, YYYY'); // "Apr 15, 2025"
 *      ```
 *
 * @param dateOrFormat - Either a date to format or a format string to apply to the current date.
 * @param format - Optional format string to apply to the provided date.
 * @returns The formatted date string.
 */
export function dateToString(format: string): string;
export function dateToString(date: dayjs.ConfigType, format?: string): string;
export function dateToString(dateOrFormat?: dayjs.ConfigType | string, format?: string): string {
	if (typeof dateOrFormat === 'string') {
		const date = dayjs(dateOrFormat);

		if (date.isValid() && format === undefined) {
			return date.format();
		}

		if (!format) {
			return dayjs().format(dateOrFormat);
		}
	}

	return dayjs(dateOrFormat).format(format);
}

export const subtractDate = (
	quantity: number,
	unit: ManipulateType,
	date?: dayjs.ConfigType,
	format?: string
): string | Dayjs => {
	if (format) {
		return dayjs(date).subtract(quantity, unit).format(format);
	}
	return dayjs(date).subtract(quantity, unit);
};

export const getDurationMilliSeconds = (durationProps: DurationUnitsObjectType): number => {
	return dayjs.duration(durationProps).asMilliseconds();
};

export const isSameDay = ({ date, current, utcOffsetMinutes = 0 }: IsSameDay): boolean => {
	const dateA = dayjs.utc(date).add(utcOffsetMinutes, 'minutes').format('YYYY-MM-DD');
	const dateB = dayjs.utc(current).add(utcOffsetMinutes, 'minutes').format('YYYY-MM-DD');
	return dayjs.utc(dateA).diff(dayjs.utc(dateB), 'day', true) === 0;
};

export const getWeekdaysTitles = (local: string | ILocale | undefined = locale.en_US.intl) => {
	dayjs.locale(local);
	return dayjs.weekdays();
};

export const validateIntervalDates = (dateIntervals: ValidateIntervalDates[], format: string = 'HH:mm'): boolean => {
	const intervals = dateIntervals.map(({ from, to }) => ({
		from: getNormalizedDateTime(from, format),
		to: getNormalizedDateTime(to, format),
	}));

	intervals.sort((a, b) => a.from.valueOf() - b.from.valueOf());

	for (let i = 0; i < intervals.length - 1; i++) {
		if (!intervals[i].to.isSameOrBefore(intervals[i + 1].from)) {
			return false;
		}
	}

	return true;
};

export const getFormattedTime = (stringTime: dayjs.ConfigType = '00:00', format?: dayjs.OptionType) => {
	const time = stringTime === '' ? '00:00' : stringTime;
	const formattedTime = dayjs(time, format);

	return formattedTime;
};

export const isValidDateTime = (
	dateValue: dayjs.ConfigType,
	format: string = YEAR_MONTH_DAY_TIME_WITH_MILISECONDS_FORMAT
): boolean => {
	const isInvalid = dayjs(dateValue, format, true).isValid();

	return isInvalid;
};

export const getEndOf = (unit: OpUnitType = 'day', date?: dayjs.ConfigType): dayjs.Dayjs => {
	return dayjs(date).endOf(unit);
};

export const getStartOf = (unit: OpUnitType = 'day', date?: dayjs.ConfigType): dayjs.Dayjs => {
	return dayjs(date).startOf(unit);
};

export const convertToUtc = (dateTime: dayjs.Dayjs, keepLocalTime: boolean = true): dayjs.Dayjs => {
	return dateTime.utc(keepLocalTime);
};

export const addTimeToDate = (date: dayjs.Dayjs = dayjs(), amount: number, unit?: ManipulateType) => {
	return date.add(amount, unit);
};

export const getDifference = (
	to: ConfigType,
	from: dayjs.ConfigType = dayjs(),
	unit: QUnitType | OpUnitType = 'milliseconds',
	returnFloat: boolean = false
): number => {
	const fromDate = dayjs(from);
	return fromDate.diff(to, unit, returnFloat);
};

export const disablePreviousDates = (
	currentDate: dayjs.Dayjs,
	disable: DisabledDates = DisabledDates.WEEKENDS_ONLY
) => {
	if (disable === DisabledDates.WEEKENDS_ONLY) {
		const dayOfWeek = currentDate.day();
		const disableWeekend = dayOfWeek === DAY.sunday || dayOfWeek === DAY.saturday;
		return disableWeekend;
	}

	const disablePastDays = currentDate.isBefore(dayjs().startOf('day'), 'day');
	if (disable === DisabledDates.ALL_PREVIOUS_DATES) {
		return disablePastDays;
	}

	const disablePastDaysAndCurrent = currentDate.isSameOrBefore(dayjs().endOf('day'), 'day');
	if (disable === DisabledDates.ALL_BEFORE_TOMORROW) {
		return disablePastDaysAndCurrent;
	}

	return false;
};

export const createDateRangeBy = (
	startDate: dayjs.ConfigType,
	endDate: dayjs.ConfigType,
	unit: ManipulateType = 'day',
	format?: string
): Dayjs[] => {
	const result = [];
	let current = dayjs(startDate, format);
	const end = dayjs(endDate, format);

	while (current.isSameOrBefore(end, unit)) {
		result.push(current);
		current = current.add(1, unit);
	}

	return result;
};

export const createDateRange = (startDate: string, endDate: string, format?: string): DateRange => {
	return {
		start: dayjs(startDate, format),
		end: dayjs(endDate, format),
	};
};

export const getDayOfWeek = (date = dayjs()): DaysOfWeek => date.day();

export const massiveCommandUtcDates = (config: ConfigType, hour: number, minute: number, value: number) => {
	return dayjs.utc(config).hour(hour).minute(minute).add(value, 'minutes');
};

export const getDuration = (units: DurationUnitsObjectType): duration.Duration => {
	return dayjs.duration(units);
};

export const getDurationFromNumber = (units: number): duration.Duration => {
	return dayjs.duration(units);
};

/**
 * Formats duration in milliseconds to human-readable time
 * @param {number} milliseconds - Duration in milliseconds
 * @param {string} formatType - Format type ('timestamp' or 'timestampWithSecondsHumanize')
 * @returns {string} Formatted duration string
 */
export const formatDuration = (
	milliseconds: number,
	formatType: TimeFormats = TimeFormats.TIMESTAMP_WITH_SECONDS
): string => {
	const d = dayjs.duration(milliseconds);

	const hours = Math.floor(d.asHours());
	const minutes = d.minutes();
	const seconds = d.seconds();

	switch (formatType) {
		case TimeFormats.TIMESTAMP_WITH_SECONDS:
			return `${hours}:${minutes}:${seconds}`;
		case TimeFormats.TIMESTAMP_SECONDS_HUMANIZED:
			return `${hours} hrs, ${minutes} mins, ${seconds} secs`;
		case TimeFormats.TIMESTAMP:
			return `${hours} hrs, ${minutes} mins`;
		case TimeFormats.TIMESTAMP_COUNTER:
			return `${minutes} mins, ${seconds} secs`;
		default:
			return `${hours}:${minutes}:${seconds}`;
	}
};

export const dateFromNow = (date: dayjs.Dayjs): string => {
	return date.fromNow();
};

export const utcDateFromNow = (config: ConfigType, offset: number | string): string => {
	return dayjs.utc(config).utcOffset(offset).fromNow();
};

export const startOfUtc = (date: dayjs.ConfigType, startOfUnit: OpUnitType, offset: number | string) => {
	return dayjs(date).startOf(startOfUnit).utcOffset(offset).utc();
};

export const endOfUtc = (date: dayjs.ConfigType, endOfUnit: OpUnitType, offset: number | string) => {
	return dayjs(date).endOf(endOfUnit).utcOffset(offset).utc();
};

export const isSameTime = (dateA: dayjs.ConfigType, dateB: ConfigType): boolean => {
	return dayjs(dateA).isSame(dateB);
};

export const setLocale = (preset?: string | ILocale, object?: Partial<ILocale>, isLocal?: boolean): string => {
	return dayjs.locale(preset, object, isLocal);
};

export const mergeDateAndTime = (date: dayjs.Dayjs, time: dayjs.Dayjs): dayjs.Dayjs => {
	return dayjs()
		.set('year', date.year())
		.set('month', date.month())
		.set('date', date.date())
		.set('hour', time.hour())
		.set('minute', time.minute())
		.set('second', time.second());
};

export const isToday = (dateToCompare?: string): boolean => {
	let isCurrentDate = false;

	if (dateToCompare) {
		const today = new Date();
		const timeDifference = getTimeDifference(dateToCompare, today, 'day');
		isCurrentDate = timeDifference === 0;
	}
	return isCurrentDate;
};

export const parseTimezone = (date: dayjs.ConfigType, ianaTimezone: string): Dayjs => dayjs.tz(date, ianaTimezone);

export const isInRange = (range: { start: ConfigType; end: ConfigType }, dateToCheck: ConfigType): boolean => {
	const start = dayjs(range.start);
	const end = dayjs(range.end);
	const date = dayjs(dateToCheck);
	return date.isBetween(start, end, null, '[]');
};

export const localToUtc = (date: dayjs.ConfigType, ianaTimezone: string): Dayjs => {
	return dayjs(date).tz(ianaTimezone).utc(true);
};

// todo: remove this and use utcToLocalIana instead
export const utcToLocal = (date: dayjs.ConfigType, offset: number): Dayjs => {
	return dayjs.utc(date).utcOffset(offset);
};

// todo: rename this to utcToLocal
export const utcToLocalIana = ({ utcDate, ianaTimezone }: UtcToLocal): Dayjs => {
	return dayjs.utc(utcDate).tz(ianaTimezone);
};

export const utcToLocalIanaWithFormat = (
	utcDate: dayjs.ConfigType,
	ianaTimezone: string,
	format = YEAR_MONTH_DAY_TIME_WITH_MILISECONDS_FORMAT
): string => {
	return dateToString(utcToLocalIana({ utcDate, ianaTimezone }), format);
};
